import { MaintenanceRecord } from './types';

export type PrintType = 'a4' | 'thermal';

export interface PrintTemplateOptions {
  printType: PrintType;
  documentType: 'receipt' | 'warranty' | 'invoice';
  systemSettings?: {
    companyName?: string;
    companyAddress?: string;
    companyPhone?: string;
    vatNumber?: string;
  };
  customerData?: {
    phone?: string;
    alternatePhone?: string;
    email?: string;
    address?: string;
  };
}

/**
 * إنشاء HTML للإيصال حسب نوع الطباعة
 */
export const generateReceiptHTML = (
  record: MaintenanceRecord, 
  options: PrintTemplateOptions
): string => {
  const { printType, systemSettings, customerData } = options;
  const currentDate = new Date().toLocaleDateString('ar-EG');
  const orderNumber = String(record.orderNumber || record.id.substring(0, 8));

  if (printType === 'thermal') {
    return generateThermalReceiptHTML(record, systemSettings, customerData, currentDate, orderNumber);
  } else {
    return generateA4ReceiptHTML(record, systemSettings, customerData, currentDate, orderNumber);
  }
};

/**
 * إنشاء HTML للضمان حسب نوع الطباعة
 */
export const generateWarrantyHTML = (
  record: MaintenanceRecord, 
  options: PrintTemplateOptions
): string => {
  const { printType, systemSettings, customerData } = options;
  const currentDate = new Date().toLocaleDateString('ar-EG');
  const orderNumber = String(record.orderNumber || record.id.substring(0, 8));

  if (printType === 'thermal') {
    return generateThermalWarrantyHTML(record, systemSettings, customerData, currentDate, orderNumber);
  } else {
    return generateA4WarrantyHTML(record, systemSettings, customerData, currentDate, orderNumber);
  }
};

/**
 * إنشاء HTML للفاتورة حسب نوع الطباعة
 */
export const generateInvoiceHTML = (
  record: MaintenanceRecord, 
  options: PrintTemplateOptions
): string => {
  const { printType, systemSettings, customerData } = options;
  const currentDate = new Date().toLocaleDateString('ar-EG');
  const orderNumber = String(record.orderNumber || record.id.substring(0, 8));

  if (printType === 'thermal') {
    return generateThermalInvoiceHTML(record, systemSettings, customerData, currentDate, orderNumber);
  } else {
    return generateA4InvoiceHTML(record, systemSettings, customerData, currentDate, orderNumber);
  }
};

/**
 * إيصال A4 - تصميم مفصل
 */
const generateA4ReceiptHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  // الحصول على معلومات الجهاز الأول
  const firstDevice = record.devices && record.devices.length > 0 ? record.devices[0] : null;
  // جلب رقم هاتف العميل
  const customerPhone = customerData?.phone || customerData?.alternatePhone || 'غير محدد';
  
  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>إيصال استلام - ${orderNumber}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
        body {
          font-family: 'Cairo', sans-serif;
          margin: 20px;
          line-height: 1.6;
          color: #333;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .company-name {
          font-size: 24px;
          font-weight: bold;
          color: #1976d2;
          margin-bottom: 10px;
        }
        .receipt-title {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .info-section {
          margin-bottom: 20px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 5px 0;
          border-bottom: 1px dotted #ccc;
        }
        .label {
          font-weight: bold;
          color: #555;
        }
        .value {
          color: #333;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          border-top: 1px solid #ccc;
          padding-top: 20px;
          font-size: 14px;
          color: #666;
        }
        .signature-section {
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
        }
        .signature-box {
          text-align: center;
          width: 200px;
        }
        .signature-line {
          border-top: 1px solid #333;
          margin-top: 40px;
          padding-top: 5px;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">${systemSettings?.companyName || 'اليكترونيات السواح'}</div>
        <div class="receipt-title">إيصال استلام جهاز للصيانة</div>
        <div>رقم الإيصا��: ${orderNumber}</div>
        <div>التاريخ: ${currentDate}</div>
      </div>

      <div class="info-section">
        <h3>معلومات العميل</h3>
        <div class="info-row">
          <span class="label">الاسم:</span>
          <span class="value">${record.customerName || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">رقم الهاتف:</span>
          <span class="value">${customerPhone}</span>
        </div>
      </div>

      <div class="info-section">
        <h3>معلومات الجهاز</h3>
        <div class="info-row">
          <span class="label">نوع الجهاز:</span>
          <span class="value">${record.deviceType || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">الماركة:</span>
          <span class="value">${firstDevice?.brand || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">الموديل:</span>
          <span class="value">${firstDevice?.model || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">حجم الشاشة:</span>
          <span class="value">${firstDevice?.size || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">الرقم التسلسلي:</span>
          <span class="value">${firstDevice?.serialNumber || 'غير محدد'}</span>
        </div>
      </div>

      <div class="info-section">
        <h3>تفاصيل الصيانة</h3>
        <div class="info-row">
          <span class="label">المشكلة المبلغ عنها:</span>
          <span class="value">${firstDevice?.issue || record.issue || 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">الملحقات المستلمة:</span>
          <span class="value">${record.accessories?.join(', ') || 'لا توجد'}</span>
        </div>
        <div class="info-row">
          <span class="label">التكلفة المبدئية:</span>
          <span class="value">${record.initialCost ? `${record.initialCost} جنيه` : 'غير محدد'}</span>
        </div>
        <div class="info-row">
          <span class="label">المبلغ المدفوع مقدماً:</span>
          <span class="value">${record.totalPaid ? `${record.totalPaid} جنيه` : '0 جنيه'}</span>
        </div>
        <div class="info-row">
          <span class="label">تاري�� التسليم المتوقع:</span>
          <span class="value">${record.estimatedDeliveryDate ? new Date(record.estimatedDeliveryDate).toLocaleDateString('ar-EG') : 'غير محدد'}</span>
        </div>
      </div>

      <div class="signature-section">
        <div class="signature-box">
          <div>توقيع العميل</div>
          <div class="signature-line"></div>
        </div>
        <div class="signature-box">
          <div>توقيع الموظف</div>
          <div class="signature-line"></div>
        </div>
      </div>

      <div class="footer">
        <div>${systemSettings?.companyAddress || 'العنوان'}</div>
        <div>هاتف: ${systemSettings?.companyPhone || '0123456789'}</div>
        <div>شكراً لثقتكم في خدماتنا</div>
      </div>
    </body>
    </html>
  `;
};

/**
 * إيصال حرارية - تصميم محسّن ومتناسق
 */
const generateThermalReceiptHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  // الحصول على معلومات الجهاز الأول
  const firstDevice = record.devices && record.devices.length > 0 ? record.devices[0] : null;
  const currentTime = new Date().toLocaleTimeString('ar-EG', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit',
    hour12: true 
  });
  // جلب رقم هاتف العميل من البيانات الممررة
  const phone = customerData?.phone || customerData?.alternatePhone || '';
  const issue = (firstDevice as any)?.issue || (record as any)?.issue || '';
  const accessories = record.accessories?.join(', ') || '';
  const receiver = record.receivedByEmployeeName || '';
  const technician = record.technicianName || '';
  const deliveryDate = record.estimatedDeliveryDate ? new Date(record.estimatedDeliveryDate).toLocaleDateString('ar-EG') : '';
  const totalCost = record.estimatedCost || record.initialCost || 0;
  const paidAmount = record.totalPaid || 0;
  const remainingAmount = totalCost - paidAmount;
  const deviceModel = firstDevice?.model || '';
  
  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>إيصال استلام - ${orderNumber}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Cairo', sans-serif;
          margin: 0;
          padding: 6px;
          font-size: 14px;
          line-height: 1.2;
          color: #000;
          width: 300px;
          max-width: 300px;
          background: #fff;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 0;
        }
        
        td {
          border: 1px solid #000;
          padding: 6px 8px;
          vertical-align: middle;
        }
        
        .header-row td {
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          padding: 8px 4px;
        }
        
        .title-cell {
          font-size: 15px;
          font-weight: 700;
          background: #fff;
        }
        
        .codes-row td {
          padding: 10px 4px;
          text-align: center;
        }
        
        .barcode-cell {
          width: 40%;
        }
        
        .logo-cell {
          width: 30%;
        }
        
        .qr-cell {
          width: 30%;
        }
        
        .company-name {
          font-size: 14px;
          font-weight: 700;
          text-align: center;
          padding: 6px;
        }
        
        .phone-row td {
          text-align: center;
          font-size: 15px;
          font-weight: 700;
          padding: 8px;
        }
        
        .label-cell {
          font-weight: 700;
          text-align: right;
          width: 35%;
          font-size: 13px;
        }
        
        .value-cell {
          text-align: center;
          font-size: 13px;
        }
        
        .full-width-label {
          font-weight: 700;
          text-align: right;
          font-size: 13px;
        }
        
        .full-width-value {
          text-align: center;
          font-size: 13px;
          min-height: 40px;
        }
        
        .financial-row td {
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          padding: 8px 6px;
        }
        
        .financial-header {
          font-size: 12px;
          font-weight: 700;
        }
        
        .financial-value {
          font-size: 14px;
          font-weight: 700;
        }
        
        .footer-note {
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          padding: 8px;
        }
        
        .footer-warning {
          text-align: center;
          font-size: 10px;
          padding: 6px;
        }
        
        #barcode {
          max-width: 100%;
          height: auto;
        }
        
        #qrcode {
          display: inline-block;
        }
        
        #qrcode img {
          display: block;
          margin: 0 auto;
        }
        
        .company-logo {
          max-width: 70px;
          max-height: 70px;
          display: block;
          margin: 0 auto;
        }
        
        @media print {
          body { 
            margin: 0; 
            padding: 4px; 
            width: 300px;
          }
          @page {
            margin: 0;
            size: 80mm auto;
          }
        }
      </style>
    </head>
    <body>
      <table>
        <!-- الصف الأول: التاريخ - العنوان - الوقت -->
        <tr class="header-row">
          <td>${currentDate}</td>
          <td class="title-cell">إيصال استلام</td>
          <td>${currentTime}</td>
        </tr>
        
        <!-- الصف الثاني: الباركود - الشعار - QR -->
        <tr class="codes-row">
          <td class="barcode-cell">
            <svg id="barcode"></svg>
            <div style="font-size: 11px; font-weight: 600; margin-top: 4px;">${orderNumber}</div>
          </td>
          <td class="logo-cell">
            ${(systemSettings as any)?.logoUrl || (systemSettings as any)?.companyLogo || (systemSettings as any)?.logo ? `
            <img class="company-logo" src="${(systemSettings as any)?.logoUrl || (systemSettings as any)?.companyLogo || (systemSettings as any)?.logo}" onerror="this.style.display='none'" alt="Logo" />
            ` : `<div class="company-name">${systemSettings?.companyName || 'مركز الصيانة'}</div>`}
          </td>
          <td class="qr-cell">
            <div id="qrcode"></div>
          </td>
        </tr>
        
        <!-- رقم الهاتف -->
        <tr class="phone-row">
          <td colspan="3">${systemSettings?.companyPhone || '01285596234'}</td>
        </tr>
        
        <!-- اسم العميل -->
        <tr>
          <td class="label-cell">اسم العميل</td>
          <td colspan="2" class="value-cell">${record.customerName || ''}</td>
        </tr>
        
        <!-- هاتف العميل -->
        <tr>
          <td class="label-cell">هاتف العميل</td>
          <td colspan="2" class="value-cell">${phone}</td>
        </tr>
        
        <!-- موديل الجهاز -->
        <tr>
          <td class="label-cell">موديل الجهاز</td>
          <td colspan="2" class="value-cell">${deviceModel}</td>
        </tr>
        
        <!-- ملحقات الجهاز -->
        <tr>
          <td colspan="3" class="full-width-label">ملحقات الجهاز</td>
        </tr>
        <tr>
          <td colspan="3" class="full-width-value">${accessories}</td>
        </tr>
        
        <!-- العطل -->
        <tr>
          <td colspan="3" class="full-width-label">العطل</td>
        </tr>
        <tr>
          <td colspan="3" class="full-width-value">${issue}</td>
        </tr>
        
        <!-- عطل إضافي -->
        <tr>
          <td colspan="3" class="full-width-label">عطل إضافي</td>
        </tr>
        <tr>
          <td colspan="3" class="full-width-value"></td>
        </tr>
        
        <!-- المستلم -->
        <tr>
          <td class="label-cell">المستلم</td>
          <td colspan="2" class="value-cell">${receiver}</td>
        </tr>
        
        <!-- م / الصيانه -->
        <tr>
          <td class="label-cell">م / الصيانه</td>
          <td colspan="2" class="value-cell">${technician}</td>
        </tr>
        
        <!-- تاريخ التسليم -->
        <tr>
          <td colspan="3" class="full-width-label">تاريخ التسليم</td>
        </tr>
        <tr>
          <td colspan="3" class="value-cell">${deliveryDate}</td>
        </tr>
        
        <!-- الصف المالي - 3 أعمدة فقط -->
        <tr class="financial-row">
          <td class="financial-header">إجمالي</td>
          <td class="financial-header">المدفوع</td>
          <td class="financial-header">المتبقي</td>
        </tr>
        <tr class="financial-row">
          <td class="financial-value">${totalCost}</td>
          <td class="financial-value">${paidAmount}</td>
          <td class="financial-value">${remainingAmount}</td>
        </tr>
        
        <!-- ملاحظة التذييل -->
        <tr>
          <td colspan="3" class="footer-note">احتفظ بالايصال للتقديمه عند الاستلام</td>
        </tr>
        
        <!-- تحذير التذييل -->
        <tr>
          <td colspan="3" class="footer-warning">المركز غير مسؤول عن الجهاز بعد صدور اكثر من شهر</td>
        </tr>
      </table>

      <!-- مكتبات QR و Barcode -->
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
      <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
      <script>
        (function(){
          try {
            var order = '${orderNumber}';
            var verifyUrl = (window.location && window.location.origin ? window.location.origin : '') + '/verify/receipt/' + order;
            
            // إنشاء QR Code
            var qrEl = document.getElementById('qrcode');
            if (qrEl && typeof QRCode !== 'undefined') {
              new QRCode(qrEl, { 
                text: verifyUrl, 
                width: 70, 
                height: 70, 
                correctLevel: QRCode.CorrectLevel.M 
              });
            }
            
            // إنشاء Barcode
            if (typeof JsBarcode !== 'undefined') { 
              JsBarcode('#barcode', order, { 
                format: 'CODE128', 
                displayValue: false, 
                width: 1.5,
                height: 35, 
                margin: 2
              }); 
            }
          } catch(e) { 
            console.error('Error generating codes:', e);
          }
        })();
      </script>
    </body>
    </html>
  `;
};

// دوال الضمان والفاتورة (مع تحديث التوقيعات)
const generateA4WarrantyHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  const firstDevice = record.devices && record.devices.length > 0 ? record.devices[0] : null;
  const customerPhone = customerData?.phone || customerData?.alternatePhone || 'غير محدد';
  
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>شهادة ضمان - ${orderNumber}</title></head><body><h1>شهادة ضمان</h1><p>رقم: ${orderNumber}</p><p>العميل: ${record.customerName}</p><p>الهاتف: ${customerPhone}</p></body></html>`;
};

const generateThermalWarrantyHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  const customerPhone = customerData?.phone || customerData?.alternatePhone || 'غير محدد';
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>ضمان - ${orderNumber}</title></head><body><h2>ضمان</h2><p>${record.customerName} - ${customerPhone}</p></body></html>`;
};

const generateA4InvoiceHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  const customerPhone = customerData?.phone || customerData?.alternatePhone || 'غير محدد';
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>فاتورة - ${orderNumber}</title></head><body><h1>فاتورة</h1><p>${record.customerName} - ${customerPhone}</p></body></html>`;
};

const generateThermalInvoiceHTML = (
  record: MaintenanceRecord,
  systemSettings: any,
  customerData: any,
  currentDate: string,
  orderNumber: string
): string => {
  const customerPhone = customerData?.phone || customerData?.alternatePhone || 'غير محدد';
  return `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>فاتورة - ${orderNumber}</title></head><body><h2>فاتورة</h2><p>${record.customerName} - ${customerPhone}</p></body></html>`;
};
